/** Memory results come from the first page of account-wide thing search, only after typing. */
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { searchThings, type MemoryThing } from "@/api/memory-focus";
import { subscribeDesktopChatAuthScope } from "@/desktop/desktop-chat-auth-scope";
import { conversationBrowserAccountKey } from "@/hooks/conversation-browser-cache";
import { translate } from "@/i18n";
import { panelsSearchMessages } from "@/i18n/messages/panels-search";
import { HUB_SEARCH_DEBOUNCE_MS } from "./use-hub-search";

export interface HubMemory {
  things: MemoryThing[];
  loading: boolean;
  error: string | null;
}

const IDLE: HubMemory = { things: [], loading: false, error: null };

export function useHubMemory(query: string, enabled: boolean): HubMemory {
  const scope = useSyncExternalStore(
    subscribeDesktopChatAuthScope,
    conversationBrowserAccountKey,
    () => null,
  );
  const q = enabled ? query.trim() : "";
  const key = q ? `${scope ?? ""}|${q}` : "";
  const [answer, setAnswer] = useState<HubMemory & { key: string }>({
    ...IDLE,
    key: "",
  });
  const request = useRef(0);

  useEffect(() => {
    const id = ++request.current;
    if (!key) return;
    const timer = window.setTimeout(() => {
      const current = () =>
        id === request.current && scope === conversationBrowserAccountKey();
      searchThings(q).then(
        (page) => {
          if (current())
            setAnswer({ things: page.items, loading: false, error: null, key });
        },
        () => {
          if (current())
            setAnswer({
              things: [],
              loading: false,
              error: translate(panelsSearchMessages, "memorySearchFailed"),
              key,
            });
        },
      );
    }, HUB_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [key, q, scope]);

  if (!key) return IDLE;
  return answer.key === key
    ? { things: answer.things, loading: false, error: answer.error }
    : { ...IDLE, loading: true };
}
